import SimplePeer from 'simple-peer';
import {
  acquireCallMedia,
  AcquiredMedia,
  CallMediaState,
  computeMediaState,
  decodeMediaState,
  encodeMediaState,
} from '../utils/callMedia';

// STUN/TURN ICE 서버 설정
const getIceServers = () => {
  const servers: RTCIceServer[] = [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ];
  const turnUrl = process.env.REACT_APP_TURN_SERVER_URL;
  const turnUser = process.env.REACT_APP_TURN_USERNAME;
  const turnCred = process.env.REACT_APP_TURN_CREDENTIAL;
  if (turnUrl && turnUser && turnCred) {
    servers.push({ urls: turnUrl, username: turnUser, credential: turnCred });
  }
  return servers;
};

/**
 * pid = 이 시그널을 낸 내 피어 인스턴스, toPid = 그 피어가 짝지은 상대 피어 인스턴스(아직 모르면 없음).
 * 재연결로 피어를 바꿔 끼우면 옛 인스턴스의 ICE 후보·answer가 늦게 도착한다 — simple-peer는 맞지 않는 후보를 받으면
 * 새 피어까지 destroy하므로(ERR_ADD_ICE_CANDIDATE) 인스턴스 id로 걸러 낸다. 구버전 앱은 id를 싣지 않는다(그때는 거르지 않는다)
 */
type SignalHandler = (payload: { toUserId: string; signal: any; pid: string; toPid?: string }) => void;

export interface SignalMeta {
  pid?: string;
  toPid?: string;
}
type StreamHandler = (userId: string, stream: MediaStream) => void;
type PeerCloseHandler = (userId: string) => void;
type PeerConnectHandler = (userId: string) => void;
type RemoteMediaStateHandler = (userId: string, state: CallMediaState) => void;

export interface CallHandlers {
  onSignal: SignalHandler;
  onRemoteStream: StreamHandler;
  /** 피어가 스스로 끊겼다(ICE 실패·상대 종료). removePeer·hangup으로 일부러 지운 피어는 부르지 않는다 */
  onPeerClose: PeerCloseHandler;
  onPeerConnect?: PeerConnectHandler;
  onRemoteMediaState?: RemoteMediaStateHandler;
  /** 브라우저의 '공유 중지' 막대 등으로 화면 공유가 끝났다 — 버튼 상태를 되돌린다 */
  onScreenShareEnded?: () => void;
}

/**
 * 영상 송출 상한(bps) — 메시라 상대 수만큼 따로 인코딩·송출한다. 인원이 늘면 상대당 상한을 낮춘다.
 * null = 브라우저 기본(제한 없음). 화면 공유는 글자가 뭉개지지 않게 따로 높게 둔다
 */
export function sendBitrateFor(peerCount: number, screenSharing: boolean): number | null {
  if (screenSharing) return 1_500_000;
  if (peerCount <= 1) return null;
  if (peerCount <= 3) return 800_000;
  return 500_000;
}

export interface LocalMediaInfo {
  hasMic: boolean;
  hasCamera: boolean;
  /** 화면 공유를 실을 비디오 송신 자리가 있다 — 대체 트랙까지 만들지 못한 경우에만 false */
  canScreenShare: boolean;
}

/** 피어 인스턴스 id — 같은 상대와의 옛 피어·새 피어를 구분하는 데만 쓴다(보안 용도 아님) */
function newPid(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

/**
 * 송신 트랜시버가 없어도 상대 미디어는 받는다(안전망).
 * 보통은 대체 트랙 덕분에 오디오·비디오 송수신 자리가 이미 있어 아무 영향이 없다.
 */
const OFFER_OPTIONS = { offerToReceiveAudio: true, offerToReceiveVideo: true };

/**
 * 화상통화 WebRTC 관리 (메시 — 상대마다 SimplePeer 1개).
 *
 * ⚠ 로컬 스트림은 언제나 오디오 1개 + 비디오 1개를 가진다. 장치가 없는 종류는 대체 트랙(무음·검은 화면, enabled=false)을
 * 끼운다. 이유: 화면 공유는 비디오 송신 자리의 트랙을 replaceTrack으로 바꾸는 방식이라 그 자리가 없으면 아무것도 보내지
 * 못하고, 송신 트랙이 없으면 offer에 미디어가 빠져 한 번도 실행된 적 없는 재협상 경로에 기대게 된다. 대체 트랙을 끼우면
 * offer/answer 모양이 장치가 다 있을 때와 같아 구버전 앱과 섞여도 안전하다.
 *
 * 카메라·마이크 켜짐 상태는 simple-peer 데이터 채널로 상대에게 직접 보낸다({t:'media'} — 서버는 모른다).
 */
class CallService {
  // userId → SimplePeer 인스턴스
  private peers: Map<string, SimplePeer.Instance> = new Map();
  // 피어 인스턴스 id — 내 것(localPid)과 짝지은 상대 것(remotePid)
  private peerMeta: WeakMap<SimplePeer.Instance, { localPid: string; remotePid?: string }> = new WeakMap();
  private localStream: MediaStream | null = null;
  private screenStream: MediaStream | null = null;
  private currentCallId: string | null = null;

  // 장치가 없어 끼운 대체 트랙 — 켜거나 상대에게 영상으로 내보내면 안 된다
  private placeholderTracks: Set<MediaStreamTrack> = new Set();
  private placeholderAudioCtx: AudioContext | null = null;

  private media = { hasMic: false, hasCamera: false, muted: false, cameraOff: false, screenSharing: false };

  private onSignal: SignalHandler | null = null;
  private onRemoteStream: StreamHandler | null = null;
  private onPeerClose: PeerCloseHandler | null = null;
  private onPeerConnect: PeerConnectHandler | null = null;
  private onRemoteMediaState: RemoteMediaStateHandler | null = null;
  private onScreenShareEnded: (() => void) | null = null;

  // ----------------------------------------
  // 콜백 등록
  // ----------------------------------------
  setHandlers(handlers: CallHandlers) {
    this.onSignal = handlers.onSignal;
    this.onRemoteStream = handlers.onRemoteStream;
    this.onPeerClose = handlers.onPeerClose;
    this.onPeerConnect = handlers.onPeerConnect ?? null;
    this.onRemoteMediaState = handlers.onRemoteMediaState ?? null;
    this.onScreenShareEnded = handlers.onScreenShareEnded ?? null;
  }

  // ----------------------------------------
  // 미디어 장치
  // ----------------------------------------
  /**
   * 카메라·마이크를 가능한 만큼 켜고, 없는 종류는 대체 트랙으로 채운다. 예외를 던지지 않는다 —
   * 둘 다 없어도 보기 전용으로 통화한다. 무엇을 못 켰는지는 결과로 돌려주고 안내는 호출자가 한다.
   */
  async prepareLocalMedia(): Promise<AcquiredMedia<MediaStreamTrack>> {
    this.releaseLocalMedia();

    const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
    const gum = md && typeof md.getUserMedia === 'function'
      ? (c: MediaStreamConstraints) => md.getUserMedia(c)
      : null;
    const perms = typeof navigator !== 'undefined' ? (navigator as any).permissions : undefined;
    const query = perms && typeof perms.query === 'function'
      ? async (name: 'camera' | 'microphone') => (await perms.query({ name })).state as string
      : null;

    const acquired = await acquireCallMedia<MediaStreamTrack>(gum, query);

    const tracks = [...acquired.tracks];
    if (!acquired.hasMic) {
      const t = this.createSilentAudioTrack();
      if (t) tracks.push(t);
    }
    if (!acquired.hasCamera) {
      const t = this.createBlankVideoTrack();
      if (t) tracks.push(t);
    }
    this.localStream = new MediaStream(tracks);
    this.media = {
      hasMic: acquired.hasMic,
      hasCamera: acquired.hasCamera,
      muted: false,
      cameraOff: false,
      screenSharing: false,
    };
    return acquired;
  }

  private createSilentAudioTrack(): MediaStreamTrack | null {
    try {
      const Ctx: typeof AudioContext | undefined =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return null;
      const ctx = new Ctx();
      if (typeof ctx.createMediaStreamDestination !== 'function') {
        ctx.close().catch(() => {});
        return null;
      }
      const track = ctx.createMediaStreamDestination().stream.getAudioTracks()[0];
      if (!track) {
        ctx.close().catch(() => {});
        return null;
      }
      track.enabled = false;
      this.placeholderAudioCtx = ctx;
      this.placeholderTracks.add(track);
      return track;
    } catch (err) {
      console.warn('[CallService] 무음 대체 트랙을 만들지 못했습니다:', err);
      return null;
    }
  }

  private createBlankVideoTrack(): MediaStreamTrack | null {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 320;
      canvas.height = 180;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      const capture = (canvas as any).captureStream;
      if (typeof capture !== 'function') return null;
      const track: MediaStreamTrack | undefined = capture.call(canvas, 1).getVideoTracks()[0];
      if (!track) return null;
      track.enabled = false;
      this.placeholderTracks.add(track);
      return track;
    } catch (err) {
      console.warn('[CallService] 검은 화면 대체 트랙을 만들지 못했습니다:', err);
      return null;
    }
  }

  private releaseLocalMedia() {
    this.localStream?.getTracks().forEach(t => t.stop());
    this.localStream = null;
    this.placeholderTracks.forEach(t => t.stop());
    this.placeholderTracks.clear();
    this.placeholderAudioCtx?.close().catch(() => {});
    this.placeholderAudioCtx = null;
  }

  getLocalStreamInstance(): MediaStream | null {
    return this.localStream;
  }

  getLocalMediaInfo(): LocalMediaInfo {
    return {
      hasMic: this.media.hasMic,
      hasCamera: this.media.hasCamera,
      canScreenShare: !!this.localStream && this.localStream.getVideoTracks().length > 0,
    };
  }

  /** 지금 상대에게 알릴 내 상태 */
  getLocalMediaState(): CallMediaState {
    return computeMediaState(this.media);
  }

  private sendMediaState(peer: SimplePeer.Instance) {
    if (!peer.connected) return;
    try {
      peer.send(encodeMediaState(this.getLocalMediaState()));
    } catch (err) {
      // 데이터 채널이 닫히는 중이면 실패한다 — 상태 알림은 부가 기능이라 통화에 영향 없음
      console.warn('[CallService] 상태 전송 실패:', err);
    }
  }

  private broadcastMediaState() {
    this.peers.forEach(peer => this.sendMediaState(peer));
  }

  // ----------------------------------------
  // 피어 연결 생성/관리
  // ----------------------------------------
  setCallId(callId: string) {
    this.currentCallId = callId;
  }

  private buildPeer(initiator: boolean, userId: string): SimplePeer.Instance {
    const peer = new SimplePeer({
      initiator,
      stream: this.localStream ?? undefined,
      trickle: true,
      config: { iceServers: getIceServers() },
      offerOptions: OFFER_OPTIONS,
    });
    // 맵에 먼저 넣는다 — 이벤트 처리기가 '이 피어가 그 상대의 현재 피어인가'로 옛 피어의 늦은 이벤트를 거른다
    this.peers.set(userId, peer);
    this.peerMeta.set(peer, { localPid: newPid() });
    this.setupPeerEvents(peer, userId);
    this.applyScreenShareToNewPeer(peer);
    return peer;
  }

  /**
   * offer를 보내는 initiator 피어 생성. replace = 이미 있는 피어를 버리고 새로 잇는다(재연결·상대의 재참가)
   */
  createOfferPeer(targetUserId: string, opts: { replace?: boolean } = {}) {
    if (this.peers.has(targetUserId)) {
      if (!opts.replace) return;
      this.removePeer(targetUserId);
    }
    this.buildPeer(true, targetUserId);
  }

  /**
   * offer를 받아서 answer를 보내는 피어 생성.
   * 연결된 피어가 있으면 그 피어에 넘기고(재협상), 연결되지 않은 피어(끊겼거나 아직 잇는 중)는 버리고 새로 받는다 —
   * 상대가 새 피어로 다시 offer했다는 뜻이다
   */
  createAnswerPeer(fromUserId: string, offerSignal: any, meta: SignalMeta = {}) {
    const existing = this.peers.get(fromUserId);
    if (existing) {
      const em = this.peerMeta.get(existing);
      const samePeerRenegotiation = existing.connected && !(existing as any).destroyed
        && (!meta.pid || !em?.remotePid || em.remotePid === meta.pid);
      if (samePeerRenegotiation) {
        this.safeSignal(existing, offerSignal);
        return;
      }
      this.removePeer(fromUserId);
    }
    const peer = this.buildPeer(false, fromUserId);
    const m = this.peerMeta.get(peer);
    if (m && meta.pid) m.remotePid = meta.pid;
    this.safeSignal(peer, offerSignal);
  }

  /**
   * 상대가 보낸 시그널 하나를 처리한다(offer면 answer 피어, 그 밖에는 현재 피어에). 옛 인스턴스의 시그널은 버린다
   */
  receiveSignal(fromUserId: string, signal: any, meta: SignalMeta = {}) {
    if (!signal || typeof signal !== 'object') return;
    if (signal.type === 'offer') {
      this.createAnswerPeer(fromUserId, signal, meta);
      return;
    }
    const peer = this.peers.get(fromUserId);
    if (!peer) return;
    const m = this.peerMeta.get(peer);
    if (m) {
      if (meta.toPid && meta.toPid !== m.localPid) return;              // 내 옛 피어에게 온 것
      if (meta.pid && m.remotePid && meta.pid !== m.remotePid) return;  // 상대 옛 피어가 낸 것
      if (meta.pid && !m.remotePid) m.remotePid = meta.pid;            // offer한 쪽이 첫 answer로 상대 인스턴스를 안다
    }
    this.safeSignal(peer, signal);
  }

  private safeSignal(peer: SimplePeer.Instance, signal: any) {
    if ((peer as any).destroyed) return;
    try {
      peer.signal(signal);
    } catch (err) {
      console.warn('[CallService] 시그널을 처리하지 못했습니다:', err);
    }
  }

  /** 그 상대와 지금 미디어가 이어져 있는가 */
  isPeerConnected(userId: string): boolean {
    const peer = this.peers.get(userId);
    return !!peer && peer.connected && !(peer as any).destroyed;
  }

  hasPeer(userId: string): boolean {
    return this.peers.has(userId);
  }

  /** 미디어는 두고 피어만 전부 버린다(통화에 새로 참가한 것으로 다시 잇는다) */
  resetPeers() {
    [...this.peers.keys()].forEach(id => this.removePeer(id));
  }

  private videoSender(peer: SimplePeer.Instance): RTCRtpSender | undefined {
    const senders = (peer as any)._pc?.getSenders?.() as RTCRtpSender[] | undefined;
    return senders?.find(s => s.track?.kind === 'video');
  }

  /** 상대 수에 맞춰 영상 송출 상한을 다시 정한다 — 피어가 이어지거나 빠질 때·화면 공유를 켜고 끌 때 */
  applySendBitrate() {
    const target = sendBitrateFor(this.peers.size, !!this.screenStream);
    this.peers.forEach(peer => {
      if (!peer.connected) return; // 협상 전에는 encodings가 비어 setParameters가 실패한다 — 'connect'에서 다시 부른다
      const sender = this.videoSender(peer);
      if (!sender || typeof sender.getParameters !== 'function') return;
      try {
        const params = sender.getParameters();
        if (!params.encodings || params.encodings.length === 0) return;
        const current = params.encodings[0].maxBitrate ?? null;
        if (current === target) return;
        if (target == null) delete params.encodings[0].maxBitrate;
        else params.encodings[0].maxBitrate = target;
        sender.setParameters(params).catch(err => console.warn('[CallService] 송출 상한 설정 실패:', err));
      } catch (err) {
        console.warn('[CallService] 송출 상한 설정 실패:', err);
      }
    });
  }

  /**
   * 공유 중에 생긴 피어(상대가 받기 전에 공유를 눌렀거나, 그룹 통화에 늦게 들어온 사람)는 localStream의 카메라·대체 트랙으로
   * 만들어지므로 화면 트랙으로 바꿔 준다. 송신 자리는 생성자의 addTrack이 이미 만들었다(협상 전 replaceTrack은 허용된다).
   */
  private applyScreenShareToNewPeer(peer: SimplePeer.Instance) {
    const screenTrack = this.screenStream?.getVideoTracks()[0];
    if (screenTrack) this.setPeerVideo(peer, screenTrack);
  }

  /** 이 피어의 비디오 송신 트랙을 바꾼다 — 화면 공유 시작·중지와 공유 중 새 피어가 같이 쓴다 */
  private setPeerVideo(peer: SimplePeer.Instance, track: MediaStreamTrack) {
    this.videoSender(peer)?.replaceTrack(track).catch(err => {
      console.warn('[CallService] 비디오 트랙을 바꾸지 못했습니다:', err);
    });
  }

  /**
   * 이미 존재하는 피어에 signal (answer/ICE) 전달
   */
  handleSignal(fromUserId: string, signal: any) {
    this.receiveSignal(fromUserId, signal);
  }

  // ⚠ 모든 처리기는 "이 피어가 아직 그 상대의 현재 피어인가"를 먼저 본다. 예전엔 피어를 바꿔 끼운 뒤 옛 피어의 늦은
  //   close가 새 피어를 맵에서 지우고 타일을 없앨 수 있었다(재연결·상대 재참가에서 실제로 난다)
  private setupPeerEvents(peer: SimplePeer.Instance, targetUserId: string) {
    const isCurrent = () => this.peers.get(targetUserId) === peer;

    // 끊김 처리는 맨 먼저 단다 — 아래 'data' 등록이 실패해도 피어가 죽으면 알려야 한다
    let gone = false;
    const handleGone = () => {
      if (gone) return;
      gone = true;
      if (!isCurrent()) return; // 일부러 지웠거나 이미 새 피어로 바꿨다
      this.peers.delete(targetUserId);
      this.applySendBitrate();
      this.onPeerClose?.(targetUserId);
    };
    peer.on('close', handleGone);
    peer.on('error', (err) => {
      console.error(`[CallService] Peer error (${targetUserId}):`, err);
      handleGone();
    });

    peer.on('signal', (signal) => {
      if (!isCurrent()) return;
      const m = this.peerMeta.get(peer);
      this.onSignal?.({ toUserId: targetUserId, signal, pid: m?.localPid || '', toPid: m?.remotePid });
    });

    peer.on('stream', (stream) => {
      if (isCurrent()) this.onRemoteStream?.(targetUserId, stream);
    });

    // 데이터 채널이 열리면 내 카메라·마이크 상태를 알린다 — 나중에 들어온 사람도 여기서 현재 상태를 받는다
    peer.on('connect', () => {
      if (!isCurrent()) return;
      this.sendMediaState(peer);
      this.applySendBitrate();
      this.onPeerConnect?.(targetUserId);
    });

    // 상태 수신은 부가 기능이라 등록이 실패해도 연결은 계속돼야 한다. simple-peer는 readable-stream Duplex라 on('data')가
    // process.nextTick을 부른다 — processShim이 빠진 환경에선 여기서 예외가 나 피어 등록까지 건너뛰었다
    try {
      peer.on('data', (chunk: unknown) => {
        if (!isCurrent()) return;
        const state = decodeMediaState(chunk);
        if (state) this.onRemoteMediaState?.(targetUserId, state);
      });
    } catch (err) {
      console.warn('[CallService] 상대 상태 수신을 켜지 못했습니다(통화는 계속됩니다):', err);
    }
  }

  /** 일부러 지운다 — onPeerClose를 부르지 않는다(맵에서 먼저 빼므로 close 처리기가 '현재 피어 아님'으로 넘어간다) */
  removePeer(userId: string) {
    const peer = this.peers.get(userId);
    if (peer) {
      this.peers.delete(userId);
      peer.destroy();
      this.applySendBitrate();
    }
  }

  // ----------------------------------------
  // 통화 컨트롤
  // ----------------------------------------
  /** @returns true = 이제 음소거됨. 마이크가 없으면 바꾸지 않고 언제나 true(대체 트랙을 켜면 안 된다) */
  toggleMute(): boolean {
    if (!this.localStream || !this.media.hasMic) return true;
    this.media.muted = !this.media.muted;
    this.localStream.getAudioTracks().forEach(t => {
      if (!this.placeholderTracks.has(t)) t.enabled = !this.media.muted;
    });
    this.broadcastMediaState();
    return this.media.muted;
  }

  /** @returns true = 이제 카메라 꺼짐. 카메라가 없으면 바꾸지 않고 언제나 true */
  toggleCamera(): boolean {
    if (!this.localStream || !this.media.hasCamera) return true;
    this.media.cameraOff = !this.media.cameraOff;
    this.localStream.getVideoTracks().forEach(t => {
      if (!this.placeholderTracks.has(t)) t.enabled = !this.media.cameraOff;
    });
    this.broadcastMediaState();
    return this.media.cameraOff;
  }

  async startScreenShare(): Promise<MediaStream | null> {
    try {
      this.screenStream = await (navigator.mediaDevices as any).getDisplayMedia({ video: true });
      const screenTrack = this.screenStream!.getVideoTracks()[0];

      // 모든 피어에게 화면 공유 트랙으로 교체 — 카메라가 없어도 대체 트랙이 비디오 송신 자리를 지키고 있다.
      // 이 뒤에 생기는 피어는 applyScreenShareToNewPeer가 맡는다
      this.peers.forEach(peer => this.setPeerVideo(peer, screenTrack));

      // 브라우저의 '공유 중지' 막대로 끝나도 버튼 상태가 따라오게 한다(예전엔 버튼이 '공유 중지'로 남았다)
      screenTrack.onended = () => {
        if (!this.screenStream) return;
        this.stopScreenShare();
        this.onScreenShareEnded?.();
      };
      this.media.screenSharing = true;
      this.broadcastMediaState();
      this.applySendBitrate();
      return this.screenStream;
    } catch {
      return null;
    }
  }

  stopScreenShare() {
    if (!this.screenStream) return;
    this.screenStream.getTracks().forEach(t => t.stop());
    this.screenStream = null;

    // 원래 카메라 트랙(카메라가 없으면 검은 화면 대체 트랙)으로 복원
    const videoTrack = this.localStream?.getVideoTracks()[0];
    if (videoTrack) {
      this.peers.forEach(peer => this.setPeerVideo(peer, videoTrack));
    }
    this.media.screenSharing = false;
    this.broadcastMediaState();
    this.applySendBitrate();
  }

  isScreenSharing(): boolean {
    return this.screenStream !== null;
  }

  // ----------------------------------------
  // 통화 종료 / 정리
  // ----------------------------------------
  hangup() {
    const peers = [...this.peers.values()];
    this.peers.clear(); // 먼저 비워 close 처리기가 onPeerClose를 부르지 않게 한다
    peers.forEach(peer => peer.destroy());

    this.releaseLocalMedia();

    this.screenStream?.getTracks().forEach(t => t.stop());
    this.screenStream = null;

    this.media = { hasMic: false, hasCamera: false, muted: false, cameraOff: false, screenSharing: false };
    this.currentCallId = null;
  }
}

export const callService = new CallService();
